// Seats each surviving candidate on the ceiling facet actually above it:
// per-XZ plane Y, flush tilt on slopes, lowest facet wins (soffit
// undersides), and NO facet above ⇒ the can is dropped rather than emitted
// floating. Design doc 2026-08-06 §6.
const { isPointInsideOrOnPolygon } = require('../geometry/pointInPolygon');

const FACET_EDGE_TOL = 0.01; // m — cans exactly under a facet seam still seat
const MIN_HORIZONTAL_NY = 0.5; // excludes soffit-drop/endcap/bridge verticals

/** Newell normal of a 3D polygon, unit length, flipped so y >= 0. */
function facetNormal(corners) {
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < corners.length; i++) {
    const p = corners[i];
    const q = corners[(i + 1) % corners.length];
    nx += (p.y - q.y) * (p.z + q.z);
    ny += (p.z - q.z) * (p.x + q.x);
    nz += (p.x - q.x) * (p.y + q.y);
  }
  const len = Math.hypot(nx, ny, nz);
  if (len < 1e-9) return null;
  const s = (ny < 0 ? -1 : 1) / len;
  return { x: nx * s, y: ny * s, z: nz * s };
}

function planeYAt(corners, normal, x, z) {
  const p0 = corners[0];
  return p0.y - (normal.x * (x - p0.x) + normal.z * (z - p0.z)) / normal.y;
}

/** Euler (THREE order 'XYZ', y = 0) aligning local +Y with unit normal n:
 * R·ŷ = (−sin rz, cos rx·cos rz, sin rx·cos rz). */
function tiltFromNormal(n) {
  if (Math.abs(n.x) < 1e-9 && Math.abs(n.z) < 1e-9) {
    return { x: 0, y: 0, z: 0 };
  }
  return {
    x: Math.atan2(n.z, n.y),
    y: 0,
    z: Math.asin(Math.max(-1, Math.min(1, -n.x))),
  };
}

function usableFacets(ceilings) {
  return (ceilings || [])
    .filter(
      (f) => Array.isArray(f.polygonCorners) && f.polygonCorners.length >= 3
    )
    .map((f) => ({
      facet: f,
      normal: facetNormal(f.polygonCorners),
      ring: f.polygonCorners.map((p) => ({ x: p.x, z: p.z })),
    }))
    .filter((e) => e.normal && e.normal.y >= MIN_HORIZONTAL_NY);
}

function lowestFacetAt(facets, x, z) {
  let best = null;
  for (const e of facets) {
    if (!isPointInsideOrOnPolygon({ x, z }, e.ring, FACET_EDGE_TOL)) continue;
    const y = planeYAt(e.facet.polygonCorners, e.normal, x, z);
    if (!best || y < best.y) best = { y, e };
  }
  return best;
}

function seatCandidates(candidates, ceilings, fallbackY, C) {
  const facets = usableFacets(ceilings);
  const seated = [];
  for (const c of candidates) {
    if (facets.length === 0) {
      if (fallbackY == null) continue;
      seated.push({
        surface: { x: c.x, y: fallbackY, z: c.z },
        normal: { x: 0, y: 1, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        ceilingId: null,
      });
      continue;
    }
    const best = lowestFacetAt(facets, c.x, c.z);
    if (!best) continue; // no ceiling above → never float a can
    const slopeDeg = (Math.acos(Math.min(1, best.e.normal.y)) * 180) / Math.PI;
    if (slopeDeg > C.MAX_SLOPE_DEG) continue;
    seated.push({
      surface: { x: c.x, y: best.y, z: c.z },
      normal: best.e.normal,
      rotation: tiltFromNormal(best.e.normal),
      ceilingId: best.e.facet.id ?? null,
    });
  }
  return seated;
}

/** Per-XZ ceiling height for the obstruction tall-test. */
function makeCeilingYLookup(ceilings, fallbackY) {
  const facets = usableFacets(ceilings);
  return (x, z) => {
    const best = lowestFacetAt(facets, x, z);
    return best ? best.y : fallbackY;
  };
}

/** Per-XZ id of the ceiling facet a light hangs from — the same lowest-facet
 *  rule `seatCandidates` seats on, for a caller that places lights at its own
 *  heights but still has to say which ceiling each one is on. null where no
 *  facet covers the point. The ceiling editor finds a facet's lights by this
 *  id, so a light stamped with anything else can't be counted or removed
 *  from that facet's panel. */
function makeCeilingIdLookup(ceilings) {
  const facets = usableFacets(ceilings);
  return (x, z) => lowestFacetAt(facets, x, z)?.e.facet.id ?? null;
}

/** Top elevation of one wall: its own corners when it has them, else its
 *  centre plus half its height. RoomPlan's merge EMPTIES `polygonCorners`
 *  on most walls (see reference-docs/structure-processing/wall-profile-
 *  recovery.md), so the centre form is the common path, not the exotic one.
 *  Where both exist they agree — `CB6F4B2C` Room 3's `AFEF061C` reads
 *  -1.299 either way. */
function wallTopY(wall) {
  const ys = (wall?.polygonCorners || [])
    .map((c) => c.y)
    .filter((y) => Number.isFinite(y));
  if (ys.length) return Math.max(...ys);
  const cy = wall?.center?.y;
  const h = wall?.dimensions?.y;
  if (Number.isFinite(cy) && Number.isFinite(h)) return cy + h / 2;
  return null;
}

/**
 * The flat-ceiling ELEVATION (world Y) to use when no ceiling facet covers a
 * candidate: the room's own ceiling record, else the highest wall top.
 * Returns null when neither exists — `null` means "decline", and both
 * consumers already honour it (`seatCandidates` drops the can, the
 * obstruction tall-test guards `ceilingY != null`). Declining is this
 * module's stated contract: no surface above ⇒ no can, never a floating one.
 *
 * It returns an ELEVATION, never a height. Three earlier rungs conflated the
 * two and put `CB6F4B2C` Room 3's three cans at y = 5.27, twelve metres above
 * their own floor and six above the tallest wall in the building:
 *   - `dimensions.y` and a bare `2.44` are lengths measured FROM the floor,
 *     and are only elevations in a building whose floors sit at y = 0. That
 *     room's storeys are at -6.76 / -3.82 / -1.17, and its first wall's
 *     `dimensions.y` of 5.2873 was returned verbatim as the ceiling's Y.
 *   - a `maxY > 0` guard rejected the CORRECT answer for being negative,
 *     which every below-zero building's ceiling is, and so fell through to
 *     the height — which is positive, and therefore looks valid.
 *   - only `walls[0]` was read, and that wall's `polygonCorners` is routinely
 *     the empty one (4 of Room 3's 5 walls are), so the good data one element
 *     over was never consulted.
 * The room having no ceiling at all is itself a failure upstream, not a
 * supported state — `generateCeilings` counts it as `ceilingsFailed`.
 */
function deriveFlatCeilingY(ceilings, walls) {
  if (ceilings?.length > 0 && ceilings[0]?.center?.y != null) {
    return ceilings[0].center.y;
  }
  const tops = (walls || []).map(wallTopY).filter((y) => Number.isFinite(y));
  return tops.length ? Math.max(...tops) : null;
}

module.exports = {
  seatCandidates,
  wallTopY,
  makeCeilingYLookup,
  makeCeilingIdLookup,
  deriveFlatCeilingY,
  tiltFromNormal,
  facetNormal,
};
